import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const RealEstateLanding = () => {
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'pageview',
      page: '/realestate',
      audience: 'RealEstate'
    });
  }, []);

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>URGAA for Real Estate - EV Charging for Residential & Commercial Properties | Go4Garage</title>
        <meta name="title" content="URGAA for Real Estate - EV Charging for Properties" />
        <meta name="description" content="Add EV charging to your properties. Increase property value by 8-12%. Revenue-generating amenity with zero capex. Managed charging stations for apartments, malls, offices." />
        <meta name="keywords" content="property EV charging, apartment charging stations, mall EV parking, commercial property charging, residential EV infrastructure, real estate charging India" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://urgaa.in/realestate" />
        <meta property="og:title" content="URGAA for Real Estate - EV Charging for Properties" />
        <meta property="og:description" content="Add EV charging to your properties. Increase value by 8-12% with zero capex." />
        <meta property="og:image" content="https://urgaa.in/images/og-realestate.jpg" />
        <meta property="og:locale" content="en_IN" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://urgaa.in/realestate" />
        <meta name="twitter:title" content="URGAA for Real Estate" />
        <meta name="twitter:description" content="EV charging infrastructure for properties" />
        <meta name="twitter:image" content="https://urgaa.in/images/twitter-realestate.jpg" />
        <meta name="twitter:site" content="@Go4Garage" />
        
        {/* Additional Meta */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://urgaa.in/realestate" />
        
        {/* Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "URGAA for Real Estate",
            "serviceType": "EV Charging Infrastructure for Properties",
            "provider": {
              "@type": "Organization",
              "name": "Go4Garage Private Limited",
              "url": "https://urgaa.in"
            },
            "areaServed": "IN",
            "description": "Turnkey EV charging solutions for residential and commercial properties"
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
        {/* Hero Section */}
        <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center">
            <div className="inline-block px-4 py-2 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold mb-6">
              🏢 300+ Properties | 8-12% Property Value Increase
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Add EV Charging.<br />Increase Property Value.
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Turnkey charging infrastructure for apartments, malls, and offices. Revenue-generating amenity with zero capex from you.
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/login" className="px-8 py-4 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold">
                Get Site Assessment
              </Link>
              <a href="#roi" className="px-8 py-4 border-2 border-orange-600 text-orange-600 rounded-lg hover:bg-orange-50 font-semibold">
                Calculate ROI
              </a>
            </div>
            <p className="text-sm text-gray-500 mt-4">Zero capex | Revenue sharing model | 30-day installation</p>
          </div>
        </section>

        {/* Value Proposition */}
        <section className="py-16 bg-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Why Add EV Charging to Your Property</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-blue-600 text-4xl mb-4">💰</div>
                <h3 className="text-xl font-bold mb-2">8-12% Value Increase</h3>
                <p className="text-gray-600">Properties with EV charging command premium pricing and faster sales/leases.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-green-600 text-4xl mb-4">✅</div>
                <h3 className="text-xl font-bold mb-2">IGBC/LEED Points</h3>
                <p className="text-gray-600">Earn green building certification points. Required for many new commercial projects.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-purple-600 text-4xl mb-4">👥</div>
                <h3 className="text-xl font-bold mb-2">Attract Premium Tenants</h3>
                <p className="text-gray-600">EV owners are high-income demographics. 40% say charging is a must-have amenity.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Common Concerns About EV Charging</h2>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow flex items-start gap-4">
                <div className="text-red-600 text-3xl">❌</div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold mb-1">"High upfront investment required"</h3>
                  <p className="text-gray-600">→ <strong className="text-green-600">Solution:</strong> Zero capex model. We install & manage. You get 30-40% revenue share.</p>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow flex items-start gap-4">
                <div className="text-red-600 text-3xl">❌</div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold mb-1">"Electrical load too high for our panel"</h3>
                  <p className="text-gray-600">→ <strong className="text-green-600">Solution:</strong> Smart load management. Charges vehicles when load is available. No panel upgrade needed.</p>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow flex items-start gap-4">
                <div className="text-red-600 text-3xl">❌</div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold mb-1">"Maintenance & operations complexity"</h3>
                  <p className="text-gray-600">→ <strong className="text-green-600">Solution:</strong> Fully managed service. 24/7 monitoring, maintenance, customer support - all handled by us.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">URGAA Turnkey Solution</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="border-2 border-orange-500 p-6 rounded-lg bg-white">
                <div className="text-orange-600 text-4xl mb-4">⚙️</div>
                <h3 className="text-xl font-bold mb-2">Complete Installation</h3>
                <p className="text-gray-600 mb-4">End-to-end turnkey setup in 30 days</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Site survey & electrical assessment</li>
                  <li>✓ DISCOM approval & load sanction</li>
                  <li>✓ Civil work, cabling, chargers</li>
                  <li>✓ Software integration & testing</li>
                </ul>
              </div>
              <div className="border-2 border-blue-500 p-6 rounded-lg bg-white">
                <div className="text-blue-600 text-4xl mb-4">📊</div>
                <h3 className="text-xl font-bold mb-2">Management Dashboard</h3>
                <p className="text-gray-600 mb-4">Track usage, revenue, and operations</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Real-time charging session monitoring</li>
                  <li>✓ Monthly revenue reports</li>
                  <li>✓ Resident/tenant usage analytics</li>
                  <li>✓ Automated billing & collections</li>
                </ul>
              </div>
              <div className="border-2 border-green-500 p-6 rounded-lg bg-white">
                <div className="text-green-600 text-4xl mb-4">🔧</div>
                <h3 className="text-xl font-bold mb-2">24/7 Managed Service</h3>
                <p className="text-gray-600 mb-4">We handle everything after installation</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Preventive maintenance (quarterly)</li>
                  <li>✓ Breakdown repairs (4-hour SLA)</li>
                  <li>✓ Customer support for residents</li>
                  <li>✓ Software updates & security patches</li>
                </ul>
              </div>
              <div className="border-2 border-purple-500 p-6 rounded-lg bg-white">
                <div className="text-purple-600 text-4xl mb-4">💳</div>
                <h3 className="text-xl font-bold mb-2">Flexible Billing Models</h3>
                <p className="text-gray-600 mb-4">Works for your property type</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Apartments: Per-unit metering</li>
                  <li>✓ Malls/Offices: Pay-per-use (₹20-40/kWh)</li>
                  <li>✓ Corporate: Monthly subscription</li>
                  <li>✓ All models: 30-40% revenue share to property</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ROI Section */}
        <section id="roi" className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-8">Revenue Potential</h2>
            <div className="bg-gradient-to-br from-orange-50 to-white p-8 rounded-lg shadow-lg">
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div>
                  <div className="text-4xl font-bold text-orange-600 mb-2">₹45K</div>
                  <div className="text-sm text-gray-600">Annual revenue per charger<br />(30% utilization)</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-green-600 mb-2">₹15K</div>
                  <div className="text-sm text-gray-600">Your share (35% revenue split)<br />per charger per year</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-blue-600 mb-2">₹1.5L</div>
                  <div className="text-sm text-gray-600">Total annual revenue<br />(10 chargers)</div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-lg font-bold mb-3">Example: 500-Unit Apartment Complex</h3>
                <ul className="text-left text-sm text-gray-600 space-y-2">
                  <li>✓ 10 AC chargers (7kW each) installed</li>
                  <li>✓ 30% utilization (3 chargers active at any time)</li>
                  <li>✓ ₹28/kWh charging rate to residents</li>
                  <li>✓ Property earns ₹1.5 lakhs/year</li>
                  <li>✓ Property value increases by 8-12% (₹2-3 crore for ₹25 crore property)</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-orange-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-4">Ready to Add EV Charging?</h2>
            <p className="text-xl mb-8">300+ properties already generating revenue with URGAA</p>
            <Link to="/login" className="px-8 py-4 bg-white text-orange-600 rounded-lg hover:bg-gray-100 font-semibold inline-block">
              Get Free Site Assessment →
            </Link>
            <p className="text-sm mt-4 opacity-90">30-day installation | Zero capex | Revenue share model</p>
          </div>
        </section>
      </div>
    </>
  );
};

export default RealEstateLanding;